import { createTerminalAuthAdapter } from 'artificelogin';
import { artificeFetchCompat } from '../../api/artificeHttp';
import { rememberSessionAccessToken } from '../../utils/sessionAccessToken';
import { refreshOroborosSession } from '../api/session';
import { oroborosBrand } from '../brand';

let authAdapter: ReturnType<typeof createTerminalAuthAdapter> | null = null;

function getAuthAdapter() {
  if (!authAdapter) {
    authAdapter = createTerminalAuthAdapter({
      fetch: artificeFetchCompat,
      artificeRedirectPath: '/auth/callback',
      onSession: ({ token }) => {
        rememberSessionAccessToken(token);
      },
    });
  }
  return authAdapter;
}

export async function hasLiveOroborosSession(): Promise<boolean> {
  const res = await refreshOroborosSession();
  return !!res.success;
}

/** If a terminal cookie session is already live, go straight to /app. */
export async function redirectToAppIfSessionExists(): Promise<boolean> {
  if (!(await hasLiveOroborosSession())) return false;
  window.location.assign(oroborosBrand.terminalPath || '/app');
  return true;
}

export type StartArtificeOAuthResult = {
  redirected: boolean;
  error?: string;
};

/** Cold-user entry: resume to /app when signed in, else open Terminal LOCK via ?signin=1. */
export async function beginOroborosLockSignIn(): Promise<{ redirected: boolean }> {
  if (await redirectToAppIfSessionExists()) {
    return { redirected: true };
  }
  const path = oroborosBrand.terminalPath || '/app';
  const url = new URL(path, window.location.origin);
  url.searchParams.set('signin', '1');
  window.location.assign(url.toString());
  return { redirected: true };
}

/**
 * Redirect to Artifice Account OAuth (Continue-with-Artifice path only).
 * Do not use for cold “Sign in” / “Login” — that must open Terminal LOCK first.
 */
export async function startArtificeOAuthSignIn(
  opts?: { rememberMe?: boolean },
): Promise<StartArtificeOAuthResult> {
  if (await redirectToAppIfSessionExists()) {
    return { redirected: true };
  }
  const auth = getAuthAdapter();
  if (typeof auth.loginWithArtifice !== 'function') {
    return { redirected: false, error: 'Artifice Account sign-in is not available.' };
  }
  const result = await auth.loginWithArtifice({ rememberMe: opts?.rememberMe !== false });
  if (result.redirected) return { redirected: true };
  return {
    redirected: false,
    error: String(result.error || 'Artifice Account sign-in failed'),
  };
}
